/**
 * Type-safe event emitter
 */

type EventHandler<T = any> = (data: T) => void;

/**
 * Generic type-safe event emitter
 */
export class EventEmitter<T extends Record<string, any>> {
  private events: Map<keyof T, Set<EventHandler>> = new Map();

  /**
   * Register an event listener
   * @param event - Event name
   * @param handler - Event handler function
   */
  on<K extends keyof T>(event: K, handler: EventHandler<T[K]>): void {
    if (!this.events.has(event)) {
      this.events.set(event, new Set());
    }
    this.events.get(event)!.add(handler);
  }

  /**
   * Register a one-time event listener
   * @param event - Event name
   * @param handler - Event handler function
   */
  once<K extends keyof T>(event: K, handler: EventHandler<T[K]>): void {
    const onceHandler = (data: T[K]) => {
      handler(data);
      this.off(event, onceHandler);
    };
    this.on(event, onceHandler);
  }

  /**
   * Remove an event listener
   * @param event - Event name
   * @param handler - Event handler function to remove
   */
  off<K extends keyof T>(event: K, handler: EventHandler<T[K]>): void {
    const handlers = this.events.get(event);
    if (handlers) {
      handlers.delete(handler);
      if (handlers.size === 0) {
        this.events.delete(event);
      }
    }
  }

  /**
   * Emit an event
   * @param event - Event name
   * @param data - Event data
   */
  emit<K extends keyof T>(event: K, data: T[K]): void {
    const handlers = this.events.get(event);
    if (handlers) {
      handlers.forEach(handler => {
        try {
          handler(data);
        } catch (error) {
          // Log error but don't throw to prevent one handler from breaking others
          console.error(`Error in event handler for ${String(event)}:`, error);
        }
      });
    }
  }

  /**
   * Remove all listeners for a specific event or all events
   * @param event - Optional event name. If not provided, removes all listeners.
   */
  removeAllListeners<K extends keyof T>(event?: K): void {
    if (event) {
      this.events.delete(event);
    } else {
      this.events.clear();
    }
  }

  /**
   * Get the number of listeners for a specific event
   * @param event - Event name
   * @returns Number of listeners
   */
  listenerCount<K extends keyof T>(event: K): number {
    return this.events.get(event)?.size || 0;
  }

  /**
   * Get all event names that have listeners
   * @returns Array of event names
   */
  eventNames(): Array<keyof T> {
    return Array.from(this.events.keys());
  }
}

